import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@mui/material/useMediaQuery';

import AddIcon from '@mui/icons-material/Add';

import Button from 'shared/atoms/Button';
import CircularSpinner from 'shared/atoms/CircularSpinner';
import HorizontalRule from 'shared/atoms/HorizontalRule';
import T from 'shared/atoms/Typography';
import ItemBox from 'shared/molecules/ItemBox';
import TransferOwnershipView from 'shared/organisms/TransferOwnershipView';

import { formatDate } from 'shared/utils';
import theme from 'shared/themes/default';

import EditGroup from '../EditGroup';
import GroupMembersTable from '../GroupMembersTable';
import SelectGroupMembersAndPreview from '../SelectGroupMembersAndPreview';
import ViewGroupMembersMobileView from '../ViewGroupMembersMobileView';

import { useOrganizationMembers } from '../../context';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  ${theme.breakpoints.mobile} {
    flex-direction: column;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m1};
  margin-top: ${theme.spacing.m3};
`;

const StyledHorizontalRule = styled(HorizontalRule)`
  margin-top: 30px;
`;

const SideBlock = styled.div`
  flex: 1;
  max-width: 50%;
  padding-left: ${(props) => (props.sidePadding ? '25px' : '0')};
  ${theme.breakpoints.mobile} {
    max-width: 100%;
  }
`;

const GroupsListWrapper = styled.div`
  margin-top: ${theme.spacing.m2};
  ${theme.breakpoints.mobile} {
    margin-top: 20px;
  }
`;

const ItemBoxWrapper = styled.div`
  margin-top: 6px;
`;

const GroupTableWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: ${(props) => (props.hasMembers ? 'row' : 'column')};
  justify-content: ${(props) => (props.hasMembers ? 'space-between' : '')};
  margin-top: 40px;
`;

const GroupMembersTableWrapper = styled.div`
  margin-top: ${theme.spacing.l};
`;

const SpinnerWrapper = styled.div`
  margin-top: 140px;
`;

const GroupsHeaderWrapper = styled.div`
  text-align: ${(props) => (props.hasMembers ? 'inherit' : 'center')};
`;

const GroupsOverview = ({ selectedManagerId }) => {
  const {
    groups: {
      editGroupFormStatus,
      editGroupMembersOpen,
      groupsInfo,
      loadingGroupsInfo,
      loadingUpdateGroupMembers,
      onDeleteGroup,
      onDeleteGroupMember,
      onSaveEditGroup,
      onTransferGroup,
      setEditGroupFormStatus,
      setEditGroupMembersOpen,
      setUpdateGroupMembersStatus,
      setTransferGroupStatus,
      transferGroupStatus,
      updateGroupMembers,
      updateGroupMembersResponse,
      updateGroupMembersStatus,
    },
    organizations: { getOrganizationManagers },
    onViewGroupActivity,
    registeredMembers,
    user,
  } = useOrganizationMembers();

  const [editGroupId, setEditGroupId] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState();
  const [transferGroupId, setTransferGroupId] = useState();
  const [transferGroupName, setTransferGroupName] = useState('');
  const [transferGroupOpen, setTransferGroupOpen] = useState(false);
  const [updatedGroupMembers, setUpdatedGroupMembers] = useState([]);

  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.mobile);

  const userId = user?.attributes?.sub;

  const groupOwnedBy = groupsInfo.find((group) => group.id === transferGroupId)
    ?.groupOwner.id;

  const onCloseEditGroup = () => {
    setEditGroupFormStatus(null);
    setEditGroupId(null);
  };

  const handleDeleteGroupMemberAccess = (user) => {
    onDeleteGroupMember(user, selectedGroupId);
  };

  const selectedGroup = groupsInfo.find(
    (group) => group.id === selectedGroupId
  );

  const registeredGroupMembers = (selectedGroup?.members || []).map(
    (organizationMembers) => organizationMembers.user
  );

  const pendingGroupMembers = (selectedGroup?.pendingInvitations || []).map(
    (invitation) => ({
      id: `invitation-${invitation.id}`,
      invitationId: invitation.id,
      isInvited: true,
      name: invitation.name,
      email: invitation.email,
    })
  );

  const groupMembers = [...registeredGroupMembers, ...pendingGroupMembers];

  const groupToEdit =
    editGroupId && groupsInfo.find((g) => g.id === editGroupId);
  const editGroupInitialValues = editGroupId && {
    groupId: groupToEdit.id,
    name: groupToEdit.name,
    description: groupToEdit.description,
  };

  useEffect(() => {
    if (
      !updateGroupMembersResponse.loading &&
      updateGroupMembersResponse.data &&
      isMobile
    ) {
      const users =
        updateGroupMembersResponse.data.updateGroupMembers.members.map(
          (member) => member.user
        );
      setUpdatedGroupMembers(
        users.sort((a, b) => a.name.localeCompare(b.name))
      );
    }
  }, [updateGroupMembersResponse.loading, updateGroupMembersResponse.data]);

  const handleUpdateGroupMembers = (users) => {
    updateGroupMembers(users, selectedGroupId);
  };

  const handleAddGroupMembersDialog = () => {
    setUpdateGroupMembersStatus(null);
    setEditGroupMembersOpen(true);
  };

  const onTransferOwnership = (groupId) => {
    const group = groupsInfo.find((g) => g.id === groupId);
    setTransferGroupOpen(true);
    setTransferGroupName(group.name);
    setTransferGroupId(groupId);
  };

  const onCloseTransferHandler = () => {
    setTransferGroupOpen(false);
    setTransferGroupStatus(null);
  };

  const onTransferOwnershipHandler = (user) => {
    onTransferGroup(transferGroupId, user[0].id);
  };

  const isLoadingSelectGroupMembersAndPreview =
    !isMobile && (!registeredMembers || loadingUpdateGroupMembers);

  const buttons = (membersCount) => [
    {
      label: t('administrateUsers.members'),
      handler: (groupId) => {
        setSelectedGroupId(groupId);
        if (!isMobile) {
          handleAddGroupMembersDialog();
        } else {
          setUpdateGroupMembersStatus(null);
        }
      },
      disabled: isLoadingSelectGroupMembersAndPreview,
    },
    {
      label: t('administrateUsers.activity'),
      handler: onViewGroupActivity,
      disabled: membersCount === 0,
    },
  ];

  const menuItems = [
    {
      handler: (groupId) => setEditGroupId(groupId),
      id: 'editGroup',
      label: t('administrateUsers.editGroup'),
    },
    {
      handler: (groupId) => onDeleteGroup({ id: groupId }),
      id: 'deleteGroup',
      label: t('administrateUsers.deleteGroup'),
    },
    {
      handler: (groupId) => onTransferOwnership(groupId),
      id: 'transferGroup',
      label: t('administrateUsers.transferOwnership'),
    },
  ];

  const initialSelectedUsers =
    updatedGroupMembers.length > 0 ? updatedGroupMembers : groupMembers || [];

  return (
    <>
      {loadingGroupsInfo ? (
        <SpinnerWrapper>
          <CircularSpinner static />
        </SpinnerWrapper>
      ) : (
        !!groupsInfo?.length && <StyledHorizontalRule />
      )}
      <Wrapper>
        <SideBlock>
          {!!groupsInfo?.length && (
            <TitleWrapper>
              <T
                color={theme.palette.lightPure}
                phoneVariant="subtitle"
                phoneWeight="medium"
                variant="headingS"
              >
                {t('administrateUsers.manageYourGroups')}
              </T>
              <T color={theme.palette.disabledLight}>
                {t('administrateUsers.addOrDeleteMembersToGroups')}
              </T>
            </TitleWrapper>
          )}
          {!!groupsInfo?.length && (
            <GroupsListWrapper>
              {groupsInfo
                .filter(
                  (group) =>
                    selectedManagerId === 'all' ||
                    group.groupOwner.id === selectedManagerId
                )
                .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                .map((group) => (
                  <ItemBoxWrapper key={group.id}>
                    <ItemBox
                      buttons={buttons(group.membersCount)}
                      id={group.id}
                      menuItems={menuItems}
                      onClickHandler={
                        isMobile
                          ? undefined
                          : (groupId) => {
                              if (selectedGroupId === groupId) {
                                setSelectedGroupId(null);
                              } else {
                                setSelectedGroupId(groupId);
                              }
                            }
                      }
                      selected={selectedGroupId === group.id}
                      theme="light"
                      title={group.name}
                      topText={`${t('administrateUsers.createdOn')} ${formatDate(
                        {
                          date: group.createdAt,
                        }
                      )}`}
                    />
                  </ItemBoxWrapper>
                ))}
            </GroupsListWrapper>
          )}
        </SideBlock>

        {!isMobile && selectedGroup && (
          <SideBlock sidePadding>
            <GroupTableWrapper hasMembers={groupMembers.length}>
              <GroupsHeaderWrapper hasMembers={groupMembers.length}>
                <T color={theme.palette.lightPure} variant="helperText">
                  {t('administrateUsers.group')}
                </T>
                <T
                  color={theme.palette.lightPure}
                  fontWeight="semibold"
                  variant="subtitle"
                >
                  {selectedGroup.name}
                </T>
              </GroupsHeaderWrapper>
              {!groupMembers.length && (
                <T color={theme.palette.disabledLight} variant="paragraphS">
                  {t('administrateUsers.emptyGroupDescription')}
                </T>
              )}
              <Button
                ariaLabel={t('administrateUsers.addMembers')}
                disabled={isLoadingSelectGroupMembersAndPreview}
                iconRight={AddIcon}
                onClick={handleAddGroupMembersDialog}
                theme="dark"
                type="secondaryOutline"
              >
                {t('administrateUsers.addMembers')}
              </Button>
            </GroupTableWrapper>
            {!!groupMembers.length && (
              <GroupMembersTableWrapper>
                <GroupMembersTable
                  groupMembers={groupMembers}
                  onDeleteUser={(row) => handleDeleteGroupMemberAccess(row)}
                  userId={userId}
                />
              </GroupMembersTableWrapper>
            )}
          </SideBlock>
        )}
        {isMobile && (
          <ViewGroupMembersMobileView
            formStatus={updateGroupMembersStatus}
            groupName={selectedGroup?.name || ''}
            initialGroupMembers={groupMembers || []}
            onClose={() => setSelectedGroupId(null)}
            onSubmitHandler={handleUpdateGroupMembers}
            open={!!selectedGroupId}
            setEditGroupMembersOpen={handleAddGroupMembersDialog}
            setUpdatedGroupMembers={setUpdatedGroupMembers}
            updatedGroupMembers={updatedGroupMembers}
          />
        )}
        {editGroupId && (
          <EditGroup
            formStatus={editGroupFormStatus}
            initialValues={editGroupInitialValues}
            onClose={onCloseEditGroup}
            onSave={(values) => onSaveEditGroup(values)}
          />
        )}
        {editGroupMembersOpen && (
          <SelectGroupMembersAndPreview
            formStatus={updateGroupMembersStatus}
            groupName={selectedGroup?.name || ''}
            initialSelected={initialSelectedUsers}
            onClose={() => setEditGroupMembersOpen(false)}
            onSubmit={handleUpdateGroupMembers}
          />
        )}
        {transferGroupOpen && (
          <TransferOwnershipView
            formStatus={transferGroupStatus}
            onSubmitHandler={onTransferOwnershipHandler}
            transferItemName={transferGroupName}
            usersToTransfer={getOrganizationManagers([groupOwnedBy])}
            onClose={onCloseTransferHandler}
          />
        )}
      </Wrapper>
    </>
  );
};

GroupsOverview.propTypes = {
  selectedManagerId: PropTypes.string.isRequired,
};

export default GroupsOverview;
